import { useFormik } from 'formik'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components/macro'
import * as Yup from 'yup'

import { getToken } from '@loadsmart/loadsmart-ui/dist/theming'

import { ButtonAction as Button } from 'legacy/components/Button/ButtonAction'
import { FormAlert } from 'legacy/components/FormAlert/FormAlert'
import { I18n } from 'legacy/components/I18n/i18n'
import { Input as InputForm } from 'legacy/components/Input/Input'
import { resetAccountPassword } from 'legacy/services/account'
import { screen } from 'legacy/styles/screen'

import { ForgotPasswordSuccess } from './ForgotPasswordSuccess'

const Wrapper = styled.div`
  width: 100%;

  ${screen.md} {
    min-width: 480px;
  }
`

const Title = styled.h2`
  width: 100%;
  margin: 0;
  margin-bottom: 24px;
  padding-bottom: 20px;

  color: ${getToken('color-neutral-darkest')};
  font-weight: bold;
  font-size: 22px;
  line-height: 24px;
  text-align: center;

  border-bottom: 1px solid ${getToken('color-neutral-light')};
`

const StyledAlert = styled(FormAlert)`
  margin-bottom: 10px;

  color: ${getToken('color-danger-dark')};

  background-color: ${getToken('color-danger-light')};
`

const InputWrapper = styled.div`
  position: relative;

  margin-bottom: 20px;
`

const Label = styled.label`
  display: block;
  margin-bottom: 4px;

  font-weight: 500;
  font-size: 12px;
  line-height: 14px;

  text-transform: uppercase;
`

const Input = styled(InputForm)`
  margin-bottom: 2px;

  ${({ error }) =>
    error &&
    css`
      border: 1px solid ${getToken('color-danger')};
    `};
`

const StyledButton = styled(Button)`
  margin-top: 10px;
`

function ForgotPassword(props) {
  const { email, onCancel, onClose } = props
  const { values, touched, errors, handleChange, handleBlur, handleSubmit, isSubmitting, status } =
    useFormik({
      initialValues: {
        email: email,
      },
      validationSchema: Yup.object().shape({
        email: Yup.string()
          .email(I18n.get('lb.outletDialog.forgotPassword.invalidEmail'))
          .required(I18n.get('lb.outletDialog.forgotPassword.requiredEmail')),
      }),
      onSubmit: async (values, { setSubmitting, setStatus }) => {
        const data = {
          email: values.email,
        }

        try {
          await resetAccountPassword(data)
          setStatus({ submitted: true })
        } catch (error) {
          setStatus({ error: I18n.get('lb.outletDialog.forgotPassword.error') })
        } finally {
          setSubmitting(false)
        }
      },
    })

  if (status?.submitted) {
    return <ForgotPasswordSuccess email={values.email} onClose={onClose} />
  }

  return (
    <Wrapper>
      <Title>{I18n.get('lb.outletDialog.forgotPassword.title')}</Title>
      <form id="forgotPasswordDialog" name="forgotPasswordDialog" onSubmit={handleSubmit}>
        {status?.error && <StyledAlert message={status?.error} />}
        <InputWrapper>
          <Label htmlFor="forgotPasswordDialog-email">
            {I18n.get('lb.outletDialog.forgotPassword.email')}
          </Label>
          <Input
            placeholder={I18n.get('lb.outletDialog.forgotPassword.email')}
            id="forgotPasswordDialog-email"
            type="email"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            name="email"
            error={errors.email && touched.email}
          />
          {errors.email && touched.email && <StyledAlert message={errors.email} />}
        </InputWrapper>
        <StyledButton
          type="submit"
          size="sm"
          width="100%"
          disabled={isSubmitting}
          activated
          square
          upperCase
        >
          {I18n.get('lb.outletDialog.forgotPassword.retrievePassword')}
        </StyledButton>
        <StyledButton
          type="button"
          size="sm"
          width="100%"
          onClick={onCancel}
          disabled={isSubmitting}
          square
          upperCase
        >
          {I18n.get('lb.outletDialog.forgotPassword.cancel')}
        </StyledButton>
      </form>
    </Wrapper>
  )
}

ForgotPassword.displayName = 'ForgotPassword'

ForgotPassword.propTypes = {
  email: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
}

ForgotPassword.defaultProps = {
  email: '',
}

export { ForgotPassword }
